import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import useRevealOnScroll from "../../general/components/common/useRevealOnScroll";
import AdminLayout from "../components/AdminLayout";
import useNewsAdminServer from "../hooks/useNewsAdminServer";
import {
    buildCategoryPath,
    fetchHomeNews,
    fetchRelatedNewsBySlug,
} from "../../fisp-tv/data/noticias";
import {
    buildAdminNewsSlug,
    fetchAdminNoteByTitle,
    fetchScheduledAdminNoteOptions,
    fetchScheduledAdminPreviewNotes,
} from "../api";
import { FISP_TV_ADMIN_HOME_PATH } from "../config";

function getItemKey(item) {
    return item?.slug || item?.id || item?.href || item?.title;
}

function buildSidebarNews(allNews, usedNews, limit = 10) {
    const usedKeys = new Set(usedNews.filter(Boolean).map(getItemKey));
    const selected = [];

    allNews.forEach((item) => {
        const key = getItemKey(item);

        if (!item || usedKeys.has(key) || selected.length >= limit) {
            return;
        }

        selected.push(item);
    });

    allNews.forEach((item) => {
        const key = getItemKey(item);

        if (!item || selected.length >= limit || selected.some((selectedItem) => getItemKey(selectedItem) === key)) {
            return;
        }

        selected.push(item);
    });

    return selected.slice(0, limit);
}

function NoticiaLink({ item, className, children }) {
    if (item.external) {
        return (
            <a href={item.href} target="_blank" rel="noreferrer" className={className}>
                {children}
            </a>
        );
    }

    return (
        <Link to={item.href} className={className}>
            {children}
        </Link>
    );
}

function NoticiaBody({ bodyHtml, fallbackText }) {
    if (bodyHtml) {
        return <div className="noticia-body" dangerouslySetInnerHTML={{ __html: bodyHtml }}></div>;
    }

    if (fallbackText) {
        return (
            <div className="noticia-body">
                <p>{fallbackText}</p>
            </div>
        );
    }

    return null;
}

function NoticiaState({ title, description }) {
    return (
        <section className="noticia-shell">
            <div className="noticia-state-card">
                <h1>{title}</h1>
                <p>{description}</p>
                <Link to={FISP_TV_ADMIN_HOME_PATH}>Volver al admin</Link>
            </div>
        </section>
    );
}

function ShareIcon({ type }) {
    if (type === "facebook") {
        return (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M13.5 22v-8h2.7l.4-3h-3.1V9.1c0-.9.3-1.6 1.7-1.6h1.5V4.8c-.3 0-1.2-.1-2.3-.1-2.3 0-3.8 1.4-3.8 4v2.3H8.5v3h2.1v8h2.9Z"></path>
            </svg>
        );
    }

    if (type === "x") {
        return (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18.9 3H22l-6.8 7.8L23 21h-6.1l-4.8-6.3L6.6 21H3.5l7.2-8.2L3 3h6.3l4.3 5.8L18.9 3Zm-1.1 16h1.7L8.3 4.9H6.5L17.8 19Z"></path>
            </svg>
        );
    }

    if (type === "whatsapp") {
        return (
            <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                <path d="M13.601 2.326A7.854 7.854 0 0 0 8.002 0C3.58 0 0 3.582 0 8.005a7.98 7.98 0 0 0 1.258 4.252L0 16l3.851-1.24a7.944 7.944 0 0 0 4.151 1.145h.003c4.422 0 8.003-3.582 8.003-8.005a7.93 7.93 0 0 0-2.407-5.574ZM8.003 14.559a6.54 6.54 0 0 1-3.326-.912l-.24-.142-2.284.736.747-2.227-.156-.228a6.53 6.53 0 0 1-1.005-3.498c0-3.626 2.95-6.578 6.58-6.578 1.756 0 3.408.683 4.651 1.927a6.533 6.533 0 0 1 1.921 4.65c-.001 3.627-2.95 6.578-6.577 6.578Zm3.592-4.915c-.197-.099-1.17-.578-1.352-.644-.182-.066-.315-.099-.448.099-.132.197-.513.644-.628.776-.116.132-.231.149-.43.05-.197-.099-.833-.307-1.586-.98-.586-.523-.981-1.17-1.096-1.367-.116-.197-.012-.304.087-.402.087-.087.197-.23.296-.347.099-.116.132-.197.197-.33.066-.132.033-.248-.017-.347-.05-.099-.446-1.073-.611-1.47-.16-.387-.323-.334-.446-.34l-.381-.007a.734.734 0 0 0-.529.248c-.182.198-.694.678-.694 1.653 0 .975.711 1.917.81 2.049.099.132 1.393 2.128 3.377 2.983.472.204.84.326 1.128.417.474.151.905.13 1.246.079.38-.057 1.17-.479 1.336-.942.165-.462.165-.859.116-.942-.05-.083-.182-.132-.38-.231Z"></path>
            </svg>
        );
    }

    if (type === "youtube") {
        return (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31.7 31.7 0 0 0 0 12a31.7 31.7 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31.7 31.7 0 0 0 24 12a31.7 31.7 0 0 0-.5-5.8ZM9.6 15.6V8.4l6.2 3.6-6.2 3.6Z"></path>
            </svg>
        );
    }

    if (type === "spotify") {
        return (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 0a12 12 0 1 0 0 24 12 12 0 0 0 0-24Zm5.5 17.3a.75.75 0 0 1-1 .25c-2.7-1.64-6.1-2.01-10.11-1.12a.75.75 0 1 1-.32-1.46c4.4-.95 8.16-.53 11.2 1.32.35.21.46.67.23 1.01Zm1.43-3.18a.94.94 0 0 1-1.28.31c-3.09-1.9-7.8-2.45-11.46-1.34a.94.94 0 1 1-.55-1.8c4.18-1.27 9.38-.66 13 1.55.45.27.59.84.29 1.28Zm.12-3.3C15.38 8.7 9.37 8.5 5.87 9.58A1.13 1.13 0 0 1 5.2 7.42c4.02-1.22 10.7-1 15 .95a1.13 1.13 0 1 1-.95 2.06Z"></path>
            </svg>
        );
    }

    return (
        <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708C16 15.487 15.474 16 14.825 16H1.175C.526 16 0 15.487 0 14.854V1.146Zm4.943 12.248V6.169H2.542v7.225h2.401Zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016Zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169H6.25c.03.678 0 7.225 0 7.225h2.401Z"></path>
        </svg>
    );
}

function NoticiaLoading() {
    return (
        <section className="noticia-shell noticia-loading-shell" role="status" aria-live="polite">
            <span className="tv-loading-announcer">Cargando nota</span>

            <div className="noticia-layout">
                <article className="noticia-article">
                    <div className="tv-skeleton noticia-loading-cat"></div>
                    <div className="tv-skeleton noticia-loading-title"></div>
                    <div className="tv-skeleton noticia-loading-title is-short"></div>
                    <div className="tv-skeleton noticia-loading-lead"></div>
                    <div className="tv-skeleton noticia-loading-lead is-short"></div>
                    <div className="noticia-meta noticia-loading-meta">
                        <div className="tv-skeleton noticia-loading-meta-line"></div>
                    </div>
                    <div className="tv-skeleton noticia-loading-media"></div>
                    <div className="noticia-body noticia-loading-body">
                        <div className="tv-skeleton noticia-loading-body-line"></div>
                        <div className="tv-skeleton noticia-loading-body-line"></div>
                        <div className="tv-skeleton noticia-loading-body-line is-short"></div>
                        <div className="tv-skeleton noticia-loading-body-line"></div>
                        <div className="tv-skeleton noticia-loading-body-line is-medium"></div>
                    </div>
                </article>

                <aside className="noticia-sidebar noticia-sidebar-loading">
                    <div className="noticia-sidebar-block noticia-loading-panel">
                        <div className="tv-skeleton noticia-loading-panel-title is-narrow"></div>
                        {[0, 1, 2, 3].map((item) => (
                            <div key={item} className="noticia-sidebar-item noticia-sidebar-item-loading">
                                <div className="noticia-sidebar-copy">
                                    <div className="tv-skeleton noticia-loading-sidebar-line"></div>
                                    <div className="tv-skeleton noticia-loading-sidebar-line is-short"></div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="noticia-follow-card noticia-loading-panel">
                        <div className="tv-skeleton noticia-loading-panel-title"></div>
                        <div className="tv-skeleton noticia-loading-panel-line"></div>
                        <div className="tv-skeleton noticia-loading-panel-line is-short"></div>
                        <div className="noticia-follow-actions">
                            <div className="tv-skeleton noticia-loading-pill"></div>
                            <div className="tv-skeleton noticia-loading-pill"></div>
                        </div>
                    </div>
                </aside>
            </div>
        </section>
    );
}

export default function FispTvAdminPreviewNote() {
    const pageRef = useRef(null);
    const navigate = useNavigate();
    const { slug } = useParams();
    const { server, serverResolved } = useNewsAdminServer();
    const [noteOptions, setNoteOptions] = useState([]);
    const [scheduledPreviewNotes, setScheduledPreviewNotes] = useState([]);
    const [homeNews, setHomeNews] = useState([]);
    const [selectedTitle, setSelectedTitle] = useState("");
    const [selectedNote, setSelectedNote] = useState(null);
    const [relatedNews, setRelatedNews] = useState([]);
    const [status, setStatus] = useState("loading");
    const [loadingNote, setLoadingNote] = useState(false);
    const previewBasePath = `${FISP_TV_ADMIN_HOME_PATH}/previsualizar-nota`;

    useRevealOnScroll(pageRef);

    useEffect(() => {
        if (!serverResolved) {
            return undefined;
        }

        const controller = new AbortController();
        setStatus("loading");

        Promise.all([
            fetchScheduledAdminNoteOptions(server, controller.signal),
            fetchScheduledAdminPreviewNotes(server, controller.signal).catch(() => []),
            fetchHomeNews(controller.signal, server).catch(() => []),
        ])
            .then(([options, scheduled, home]) => {
                setNoteOptions(options);
                setScheduledPreviewNotes(scheduled);
                setHomeNews(home);
                setStatus("ready");
            })
            .catch(() => {
                setNoteOptions([]);
                setScheduledPreviewNotes([]);
                setHomeNews([]);
                setStatus("error");
            });

        return () => controller.abort();
    }, [server, serverResolved]);

    const sidebarNews = useMemo(() => {
        return selectedNote ? buildSidebarNews(homeNews, [selectedNote], 10) : [];
    }, [homeNews, selectedNote]);

    useEffect(() => {
        if (status === "loading" || status === "error") {
            return;
        }

        if (!noteOptions.length) {
            setSelectedTitle("");
            setSelectedNote(null);
            setRelatedNews([]);
            return;
        }

        if (!slug) {
            const firstScheduledSlug = scheduledPreviewNotes[0]?.slug;
            const firstTitle = noteOptions[0]?.title;

            if (firstScheduledSlug) {
                navigate(`${previewBasePath}/${firstScheduledSlug}`, { replace: true });
                return;
            }

            if (firstTitle) {
                navigate(`${previewBasePath}/${buildAdminNewsSlug(firstTitle)}`, { replace: true });
            }

            return;
        }

        const matchedOption = noteOptions.find((item) => buildAdminNewsSlug(item.title) === slug) || null;

        if (!matchedOption) {
            setSelectedTitle("");
            setSelectedNote(null);
            setRelatedNews([]);
            setStatus("missing");
            return;
        }

        if (selectedTitle !== matchedOption.title) {
            setSelectedTitle(matchedOption.title);
        }
    }, [navigate, noteOptions, previewBasePath, scheduledPreviewNotes, selectedTitle, slug, status]);

    useEffect(() => {
        if (!serverResolved || !selectedTitle) {
            return undefined;
        }

        const controller = new AbortController();
        const noteSlug = buildAdminNewsSlug(selectedTitle);

        setLoadingNote(true);
        setSelectedNote(null);
        setRelatedNews([]);

        Promise.all([
            fetchAdminNoteByTitle(selectedTitle, server, controller.signal),
            fetchRelatedNewsBySlug(noteSlug, 4, controller.signal, server).catch(() => []),
        ])
            .then(([payload, related]) => {
                if (!payload?.normalized) {
                    setSelectedNote(null);
                    setRelatedNews([]);
                    setStatus("missing");
                    return;
                }

                setSelectedNote(payload.normalized);
                setRelatedNews(related);
                setStatus("ready");
            })
            .catch((error) => {
                if (error.name === "AbortError") {
                    return;
                }

                setSelectedNote(null);
                setRelatedNews([]);
                setStatus("error");
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setLoadingNote(false);
                }
            });

        return () => controller.abort();
    }, [selectedTitle, server, serverResolved]);

    useEffect(() => {
        if (!selectedNote) {
            return undefined;
        }

        const previousTitle = document.title;
        document.title = `${selectedNote.title} | Preview FISP TV`;

        return () => {
            document.title = previousTitle;
        };
    }, [selectedNote]);

    const currentUrl = typeof window !== "undefined" ? window.location.href : "";
    const encodedUrl = encodeURIComponent(currentUrl);
    const encodedTitle = encodeURIComponent(selectedNote?.title || "FISP TV");
    const categoryPath = selectedNote ? buildCategoryPath(selectedNote.category) : "/fisp-tv";

    return (
        <AdminLayout title="Previsualizar nota" pageClassName="tv-admin-preview-note-route">
            <div className="noticia-page" ref={pageRef}>
                <section className="tv-admin-preview-toolbar-shell">
                    {status === "ready" && !scheduledPreviewNotes.length ? (
                        <p className="tv-admin-preview-empty">No hay notas programadas</p>
                    ) : null}

                    {scheduledPreviewNotes.length ? (
                        <div className="tv-admin-scheduled-strip">
                            <div className="tv-admin-scheduled-strip-head">
                                <h2>Programadas</h2>
                            </div>

                            <div className="tv-admin-scheduled-strip-list">
                                {scheduledPreviewNotes.map((item) => {
                                    const isActive = item.slug === slug;

                                    return (
                                        <button
                                            key={item.id || item.slug}
                                            type="button"
                                            className={`tv-admin-scheduled-preview-card${isActive ? " is-active" : ""}`}
                                            onClick={() => navigate(`${previewBasePath}/${item.slug}`)}
                                        >
                                            <span className="tv-admin-scheduled-preview-thumb">
                                                {item.image ? <img src={item.image} alt={item.title} /> : <span className="tv-admin-scheduled-preview-thumb-fallback"></span>}
                                            </span>
                                            <span className="tv-admin-scheduled-preview-copy">
                                                <span>{item.title}</span>
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}
                </section>

                {status === "loading" || loadingNote ? <NoticiaLoading /> : null}

                {status === "error" ? (
                    <NoticiaState title="No se pudo cargar la previsualización" description="Hubo un problema consultando las notas del admin. Probá nuevamente en unos minutos." />
                ) : null}

                {status === "missing" ? (
                    <NoticiaState title="Nota no encontrada" description="La nota que querés previsualizar no aparece en la base actual del admin." />
                ) : null}

                {status === "ready" && selectedNote ? (
                    <section className="noticia-shell">
                        <div className="noticia-layout">
                            <article className="noticia-article reveal">
                                <Link to={categoryPath} className="noticia-cat">
                                    {selectedNote.category}
                                </Link>
                                <h1>{selectedNote.title}</h1>
                                <p className="noticia-lead">{selectedNote.description}</p>

                                {selectedNote.publishedAt ? (
                                    <div className="noticia-meta">
                                        <span>{new Date(selectedNote.publishedAt).toLocaleDateString("es-AR")}</span>
                                    </div>
                                ) : null}

                                {selectedNote.embedUrl ? (
                                    <div className="noticia-video">
                                        <iframe
                                            src={selectedNote.embedUrl}
                                            title={selectedNote.title}
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                        ></iframe>
                                    </div>
                                ) : selectedNote.image ? (
                                    <div className="noticia-cover">
                                        <img src={selectedNote.image} alt={selectedNote.title} />
                                    </div>
                                ) : null}

                                <NoticiaBody bodyHtml={selectedNote.bodyHtml} fallbackText={selectedNote.description} />

                                <div className="noticia-tags">
                                    <Link to={categoryPath}>{selectedNote.category}</Link>
                                    <Link to="/fisp-tv">FISP TV</Link>
                                </div>

                                <div className="noticia-share">
                                    <span>Compartir</span>
                                    <a
                                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="share-btn fb"
                                        aria-label="Compartir en Facebook"
                                    >
                                        <ShareIcon type="facebook" />
                                    </a>
                                    <a
                                        href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="share-btn tw"
                                        aria-label="Compartir en X"
                                    >
                                        <ShareIcon type="x" />
                                    </a>
                                    <a
                                        href={`https://wa.me/?text=${encodeURIComponent(`${selectedNote.title} ${currentUrl}`)}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="share-btn wa"
                                        aria-label="Compartir en WhatsApp"
                                    >
                                        <ShareIcon type="whatsapp" />
                                    </a>
                                    <a
                                        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="share-btn ln"
                                        aria-label="Compartir en LinkedIn"
                                    >
                                        <ShareIcon type="linkedin" />
                                    </a>
                                </div>

                                {relatedNews.length ? (
                                    <div className="noticia-related reveal">
                                        <h3>Notas relacionadas</h3>

                                        <div className="noticia-related-grid">
                                            {relatedNews.map((item, index) => (
                                                <NoticiaLink key={getItemKey(item) || `${item.title}-${index}`} item={item} className="noticia-related-card">
                                                    <div className="noticia-related-card-media">
                                                        {item.image ? <img src={item.image} alt={item.title} /> : <div className={`ph bg${(index % 10) + 1}`}></div>}
                                                        <span className="play-sm">
                                                            <svg viewBox="0 0 24 24" fill="currentColor">
                                                                <polygon points="5 3 19 12 5 21"></polygon>
                                                            </svg>
                                                        </span>
                                                    </div>
                                                    <div className="noticia-related-card-body">
                                                        <h4>{item.title}</h4>
                                                        <p>{item.publishedAt ? new Date(item.publishedAt).toLocaleDateString("es-AR") : item.category}</p>
                                                    </div>
                                                </NoticiaLink>
                                            ))}
                                        </div>
                                    </div>
                                ) : null}
                            </article>

                            <aside className="noticia-sidebar reveal">
                                {sidebarNews.length ? (
                                    <div className="noticia-sidebar-block">
                                        <h3>Mas vistos</h3>

                                        {sidebarNews.map((item, index) => (
                                            <NoticiaLink key={getItemKey(item) || `${item.title}-${index}`} item={item} className="noticia-sidebar-item">
                                                <div className="noticia-sidebar-copy">
                                                    <h4>{item.title}</h4>
                                                    <span>{item.category}</span>
                                                </div>
                                            </NoticiaLink>
                                        ))}
                                    </div>
                                ) : null}

                                <div className="noticia-follow-card">
                                    <h3>Seguí a FISP TV</h3>
                                    <p>Entrevistas, episodios, clips y coberturas especiales en nuestras plataformas.</p>
                                    <div className="noticia-follow-actions">
                                        <a href="https://www.youtube.com/@fisptv" target="_blank" rel="noreferrer" aria-label="YouTube de FISP TV" title="YouTube de FISP TV">
                                            <ShareIcon type="youtube" />
                                        </a>
                                        <a href="https://open.spotify.com/show/5VlWWkUHnHON4PIodMQw79" target="_blank" rel="noreferrer" aria-label="Spotify de FISP TV" title="Spotify de FISP TV">
                                            <ShareIcon type="spotify" />
                                        </a>
                                    </div>
                                </div>
                            </aside>
                        </div>
                    </section>
                ) : null}
            </div>
        </AdminLayout>
    );
}
